import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Lock,
  Pin,
  Send,
  Trash2,
  Pencil,
  X,
  Check,
  Heart,
  MessageSquare,
  CornerDownRight,
} from "lucide-react";
import {
  useForumThreadQuery,
  useForumCategoriesQuery,
  useReplyToForumThreadMutation,
  useUpdateForumThreadMutation,
  useDeleteForumThreadMutation,
  useUpdateForumPostMutation,
  useDeleteForumPostMutation,
  usePinForumThreadMutation,
  useLockForumThreadMutation,
  useLikeForumThreadMutation,
  useLikeForumPostMutation,
  useForumImage,
  coverAttachment,
  FORUM_CATEGORIES,
  type ForumPerson,
  type ForumPost as Reply,
} from "../api/forumApi";
import { AttachmentList, AttachmentPicker } from "../components/Attachments";
import {
  Avatar,
  Byline,
  PrimaryButton,
  SecondaryButton,
  IconButton,
  NAVY,
  GOLD,
  LINE,
} from "../components/People";

/** Plain text, line breaks kept. Never HTML — see NewThreadPage. */
function Body({ text, className = "" }: { text: string; className?: string }) {
  return (
    <p className={`whitespace-pre-wrap leading-relaxed text-gray-700 ${className}`}>
      {text}
    </p>
  );
}

/**
 * The heart, wherever it appears.
 *
 * The count is whatever the cache currently says: the mutation patches it
 * optimistically and writes the server's own number over the guess when it
 * lands, so this holds no state of its own.
 */
function LikeButton({
  liked,
  count,
  onClick,
  label,
}: {
  liked: boolean;
  count: number;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={liked ? "Remove your like" : label}
      className="inline-flex items-center gap-2 text-[14px] font-bold transition-colors"
      style={{ color: liked ? "#C8503C" : "#6B7480" }}
    >
      <Heart className="h-[18px] w-[18px]" fill={liked ? "currentColor" : "none"} />
      {count}
    </button>
  );
}

/**
 * Everything a comment needs that is not the comment.
 *
 * Bundled into one object so `Comment` can live at module scope. It has to:
 * a component declared inside the page is a new function on every render,
 * which React unmounts and replaces — and the reply box inside it would
 * lose the cursor after every character typed.
 */
type CommentContext = {
  locked: boolean;
  busy: boolean;
  editingPost: number | null;
  draftPost: string;
  replyingTo: number | null;
  replyBody: string;
  setEditingPost: (id: number | null) => void;
  setDraftPost: (body: string) => void;
  setReplyingTo: (id: number | null) => void;
  setReplyBody: (body: string) => void;
  onSaveEdit: (post: Reply) => void;
  onWithdraw: (post: Reply) => void;
  onLike: (post: Reply) => void;
  onPostReply: (parentId: number) => void;
};

/** Every author who has said anything here, the opener first. */
function participants(
  author: ForumPerson | null,
  posts: Reply[],
): ForumPerson[] {
  const seen = new Set<number>();
  const people: ForumPerson[] = [];

  const add = (p?: ForumPerson | null) => {
    if (!p || seen.has(p.id)) return;
    seen.add(p.id);
    people.push(p);
  };

  add(author);
  posts.forEach((post) => {
    add(post.author);
    post.replies?.forEach((r) => add(r.author));
  });

  return people;
}

/* ── One comment, and its answers ────────────────────────────────────── */

function Comment({
  post,
  nested = false,
  ctx,
}: {
  post: Reply;
  nested?: boolean;
  ctx: CommentContext;
}) {
  const editing = ctx.editingPost === post.id;

  return (
    <div
      className={nested ? "mt-5 border-l-2 pl-5" : ""}
      style={nested ? { borderColor: LINE } : undefined}
    >
      <div className="flex items-start gap-3">
        <Avatar person={post.author} size={nested ? 36 : 44} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <Byline person={post.author} at={post.created_at} size={nested ? "sm" : "base"} />

            <div className="flex shrink-0 items-center gap-1">
              {post.can_edit && !editing && (
                <button
                  type="button"
                  title="Edit"
                  onClick={() => {
                    ctx.setEditingPost(post.id);
                    ctx.setDraftPost(post.body);
                  }}
                  className="rounded-lg p-1.5 text-gray-300 hover:text-gray-600"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {post.can_delete && (
                <button
                  type="button"
                  title="Withdraw"
                  onClick={() => ctx.onWithdraw(post)}
                  className="rounded-lg p-1.5 text-gray-300 hover:text-rose-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {editing ? (
            <div className="mt-3 space-y-3">
              <textarea
                value={ctx.draftPost}
                onChange={(e) => ctx.setDraftPost(e.target.value)}
                rows={5}
                className="w-full rounded-lg border border-gray-200 px-4 py-3 text-[15px] leading-relaxed focus:border-gray-400 focus:outline-none"
              />
              <div className="flex justify-end gap-2">
                <SecondaryButton size="sm" onClick={() => ctx.setEditingPost(null)}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton size="sm" onClick={() => ctx.onSaveEdit(post)}>
                  <Check className="h-3.5 w-3.5" />
                  Save
                </PrimaryButton>
              </div>
            </div>
          ) : (
            <>
              <Body text={post.body} className="mt-2 text-[16px]" />
              <AttachmentList items={post.attachments} />

              <div className="mt-3 flex items-center gap-6">
                <LikeButton
                  liked={!!post.liked_by_me}
                  count={post.likes_count ?? 0}
                  label="Like this comment"
                  onClick={() => ctx.onLike(post)}
                />

                {/* Only on a top-level comment: the discussion nests one
                    level and no further — see the migration — so an answer
                    to an answer joins the same group. */}
                {!nested && !ctx.locked && (
                  <button
                    type="button"
                    onClick={() => {
                      ctx.setReplyingTo(ctx.replyingTo === post.id ? null : post.id);
                      ctx.setReplyBody("");
                    }}
                    className="inline-flex items-center gap-2 text-[14px] font-bold text-[#6B7480] hover:text-[#0F2A4D]"
                  >
                    <MessageSquare className="h-[18px] w-[18px]" />
                    Reply
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── Answers ────────────────────────────────────────────── */}
          {post.replies?.map((child) => (
            <Comment key={child.id} post={child} nested ctx={ctx} />
          ))}

          {/* ── Answering this one ─────────────────────────────────── */}
          {ctx.replyingTo === post.id && (
            <div className="mt-5 border-l-2 pl-5" style={{ borderColor: LINE }}>
              <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400">
                <CornerDownRight className="h-3.5 w-3.5" />
                Replying to {post.author?.name ?? "this comment"}
              </div>
              <textarea
                value={ctx.replyBody}
                onChange={(e) => ctx.setReplyBody(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Write your reply…"
                className="mt-2 w-full rounded-lg border border-gray-200 px-4 py-3 text-[15px] leading-relaxed focus:border-gray-400 focus:outline-none"
              />
              <div className="mt-2 flex justify-end gap-2">
                <SecondaryButton size="sm" onClick={() => ctx.setReplyingTo(null)}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton size="sm" disabled={ctx.busy} onClick={() => ctx.onPostReply(post.id)}>
                  {ctx.busy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                  Post reply
                </PrimaryButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ForumThreadPage() {
  const { id } = useParams();
  const threadId = Number(id);
  const navigate = useNavigate();

  const { data: thread, isLoading, isError, error } = useForumThreadQuery(threadId);

  const [reply, { isLoading: replying }] = useReplyToForumThreadMutation();
  const [updateThread, { isLoading: savingThread }] = useUpdateForumThreadMutation();
  const [deleteThread] = useDeleteForumThreadMutation();
  const [updatePost] = useUpdateForumPostMutation();
  const [deletePost] = useDeleteForumPostMutation();
  const [pin] = usePinForumThreadMutation();
  const [lock] = useLockForumThreadMutation();
  const [likeThread] = useLikeForumThreadMutation();
  const [likePost] = useLikeForumPostMutation();

  const { data: catalogue } = useForumCategoriesQuery();
  const categories = catalogue?.categories ?? FORUM_CATEGORIES;

  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState<string | null>(null);

  const [editingThread, setEditingThread] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftCategory, setDraftCategory] = useState("general");

  const [editingPost, setEditingPost] = useState<number | null>(null);
  const [draftPost, setDraftPost] = useState("");

  // Which comment the inline composer is currently open under.
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyBody, setReplyBody] = useState("");

  // The picture the discussion leads with: the first image attached to the
  // opening post. Fetched with the bearer token, because forum files are
  // private — see useForumImage.
  const cover = useForumImage(coverAttachment(thread?.attachments)?.url);

  const posts = thread?.posts ?? [];
  const people = useMemo(
    () => participants(thread?.author ?? null, posts),
    [thread?.author, posts],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-300">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (isError || !thread) {
    const status = (error as any)?.status;
    return (
      <div className="mx-auto max-w-lg px-6 py-24 text-center">
        <p className="text-sm font-bold text-gray-600">
          {status === 403
            ? "This discussion is open to the Policy Committee only."
            : "That discussion could not be opened."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/forum")}
          className="mt-4 text-[10px] font-black uppercase tracking-[0.15em] text-gray-400 hover:text-gray-600"
        >
          Back to the forum
        </button>
      </div>
    );
  }

  const category = categories.find((c) => c.key === thread.category);

  const say = async (fn: () => Promise<unknown>, fallback: string) => {
    setProblem(null);
    try {
      await fn();
    } catch (e: any) {
      setProblem(e?.data?.message || e?.error || fallback);
    }
  };

  const postComment = () => {
    if (!body.trim()) {
      setProblem("Write something first.");
      return;
    }

    return say(async () => {
      await reply({ threadId, body: body.trim(), files }).unwrap();
      setBody("");
      setFiles([]);
    }, "The comment could not be posted.");
  };

  const postReply = (parentId: number) => {
    if (!replyBody.trim()) {
      setProblem("Write something first.");
      return;
    }

    return say(async () => {
      await reply({ threadId, body: replyBody.trim(), parentId }).unwrap();
      setReplyBody("");
      setReplyingTo(null);
    }, "The reply could not be posted.");
  };

  const saveThread = () =>
    say(async () => {
      await updateThread({
        id: threadId,
        title: draftTitle.trim(),
        body: draftBody.trim(),
        category: draftCategory,
      }).unwrap();
      setEditingThread(false);
    }, "The discussion could not be saved.");

  const withdrawThread = () =>
    say(async () => {
      if (!window.confirm("Withdraw this discussion and all its replies?")) return;
      await deleteThread(threadId).unwrap();
      navigate("/forum");
    }, "The discussion could not be withdrawn.");

  // Everything the comment tree needs, in one object — see CommentContext
  // for why Comment cannot simply close over all of this.
  const ctx: CommentContext = {
    locked: thread.is_locked,
    busy: replying,
    editingPost,
    draftPost,
    replyingTo,
    replyBody,
    setEditingPost,
    setDraftPost,
    setReplyingTo,
    setReplyBody,
    onSaveEdit: (post) =>
      say(async () => {
        await updatePost({ id: post.id, threadId, body: draftPost.trim() }).unwrap();
        setEditingPost(null);
      }, "The comment could not be saved."),
    onWithdraw: (post) =>
      say(async () => {
        if (!window.confirm("Withdraw this comment?")) return;
        await deletePost({ id: post.id, threadId }).unwrap();
      }, "The comment could not be withdrawn."),
    onLike: (post) =>
      say(
        () => likePost({ id: post.id, threadId, liked: !post.liked_by_me }).unwrap(),
        "That like did not go through.",
      ),
    onPostReply: postReply,
  };

  return (
    <div className="pb-16">
      {/* ══ Hero ═════════════════════════════════════════════════════ */}
      <div className="border-b bg-[#F7F8FA]" style={{ borderColor: LINE }}>
        <div className={`grid items-stretch ${cover ? "lg:grid-cols-2" : "grid-cols-1"}`}>
          <div className="px-6 py-12 md:px-10 lg:py-16">
            <nav className="flex flex-wrap items-center gap-2 text-[14px] text-gray-500">
              <Link to="/forum" className="hover:underline">
                Forums
              </Link>
              <span>/</span>
              <Link
                to={`/forum?category=${thread.category}`}
                className="font-bold hover:underline"
                style={{ color: NAVY }}
              >
                {thread.category_label || "General"}
              </Link>
            </nav>

            <div className="mt-6 flex items-center gap-3">
              <span className="block h-[3px] w-8" style={{ backgroundColor: GOLD }} />
              <span className="text-[12px] font-black uppercase tracking-[0.18em] text-gray-500">
                Policy Committee Forum
              </span>
            </div>

            <h1
              className="mt-5 max-w-2xl text-[34px] font-black leading-[1.1] tracking-tight md:text-[44px]"
              style={{ color: NAVY }}
            >
              {thread.title}
            </h1>

            {(thread.is_pinned || thread.is_locked) && (
              <div className="mt-6 flex flex-wrap items-center gap-2">
                {thread.is_pinned && (
                  <span
                    className="inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em]"
                    style={{ backgroundColor: "#F6E7C6", color: "#8A6516" }}
                  >
                    <Pin className="h-3 w-3" />
                    Pinned
                  </span>
                )}
                {thread.is_locked && (
                  <span className="inline-flex items-center gap-1.5 rounded bg-gray-200 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-gray-600">
                    <Lock className="h-3 w-3" />
                    Closed
                  </span>
                )}
              </div>
            )}
          </div>

          {cover && (
            <div
              className="min-h-[220px] bg-cover bg-center lg:min-h-[420px]"
              style={{ backgroundImage: `url(${cover})` }}
              role="img"
              aria-label="Attached to this discussion"
            />
          )}
        </div>
      </div>

      {/* ══ Discussion ═══════════════════════════════════════════════ */}
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => navigate("/forum")}
              className="mb-5 inline-flex items-center gap-2 text-[14px] font-bold text-gray-500 hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              All discussions
            </button>

            {/* ── Opening post ────────────────────────────────────── */}
            <div className="rounded-lg border bg-white px-6 py-7 md:px-8" style={{ borderColor: LINE }}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <Avatar person={thread.author} size={48} />
                  <Byline person={thread.author} at={thread.created_at} />
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {thread.can_moderate && (
                    <>
                      <IconButton
                        active={thread.is_pinned}
                        title={thread.is_pinned ? "Unpin" : "Pin to the top"}
                        onClick={() =>
                          say(
                            () => pin({ id: threadId, pinned: !thread.is_pinned }).unwrap(),
                            "Could not pin.",
                          )
                        }
                      >
                        <Pin className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton
                        active={thread.is_locked}
                        title={thread.is_locked ? "Unlock" : "Close — no further comments"}
                        onClick={() =>
                          say(
                            () => lock({ id: threadId, locked: !thread.is_locked }).unwrap(),
                            "Could not close.",
                          )
                        }
                      >
                        <Lock className="h-3.5 w-3.5" />
                      </IconButton>
                    </>
                  )}
                  {thread.can_edit && !editingThread && (
                    <>
                      <IconButton
                        title="Edit"
                        onClick={() => {
                          setDraftTitle(thread.title);
                          setDraftBody(thread.body);
                          setDraftCategory(thread.category || "general");
                          setEditingThread(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton
                        title="Withdraw"
                        onClick={withdrawThread}
                        className="hover:!text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </IconButton>
                    </>
                  )}
                </div>
              </div>

              {editingThread ? (
                <div className="mt-5 space-y-3">
                  <input
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-[15px] font-black focus:border-gray-400 focus:outline-none"
                  />
                  <select
                    value={draftCategory}
                    onChange={(e) => setDraftCategory(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-gray-400 focus:outline-none"
                  >
                    {categories.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <textarea
                    value={draftBody}
                    onChange={(e) => setDraftBody(e.target.value)}
                    rows={10}
                    className="w-full rounded-lg border border-gray-200 px-4 py-3 text-[15px] leading-relaxed focus:border-gray-400 focus:outline-none"
                  />
                  <div className="flex justify-end gap-2">
                    <SecondaryButton size="sm" onClick={() => setEditingThread(false)}>
                      <X className="h-3.5 w-3.5" />
                      Cancel
                    </SecondaryButton>
                    <PrimaryButton size="sm" onClick={saveThread} disabled={savingThread}>
                      <Check className="h-3.5 w-3.5" />
                      Save
                    </PrimaryButton>
                  </div>
                </div>
              ) : (
                <>
                  <Body text={thread.body} className="mt-5 text-[17px]" />
                  <AttachmentList items={thread.attachments} />

                  <div
                    className="mt-6 flex items-center gap-7 border-t pt-5"
                    style={{ borderColor: LINE }}
                  >
                    <LikeButton
                      liked={!!thread.liked_by_me}
                      count={thread.likes_count ?? 0}
                      label="Like this discussion"
                      onClick={() =>
                        say(
                          () =>
                            likeThread({ id: threadId, liked: !thread.liked_by_me }).unwrap(),
                          "That like did not go through.",
                        )
                      }
                    />
                    <span className="inline-flex items-center gap-2 text-[14px] font-bold text-[#6B7480]">
                      <MessageSquare className="h-[18px] w-[18px]" />
                      {thread.replies_count}{" "}
                      {thread.replies_count === 1 ? "comment" : "comments"}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* ── Comments ────────────────────────────────────────── */}
            <h2 className="mb-6 mt-10 text-[26px] font-black tracking-tight" style={{ color: NAVY }}>
              {thread.replies_count}{" "}
              {thread.replies_count === 1 ? "comment" : "comments"}
            </h2>

            {posts.length === 0 ? (
              <p className="text-[15px] text-gray-500">
                Nobody has commented yet.
                {!thread.is_locked && " Be the first to answer."}
              </p>
            ) : (
              <div>
                {posts.map((post, i) => {
                  // The line between what the reader had seen and what
                  // arrived since. Drawn on the group, since a reply
                  // inside one is read as part of it.
                  const isNew =
                    !!thread.last_read_at &&
                    new Date(post.created_at).getTime() >
                      new Date(thread.last_read_at).getTime();

                  return (
                    <div key={post.id}>
                      {isNew && (
                        <div className="my-6 flex items-center gap-3">
                          <div className="h-px flex-1" style={{ backgroundColor: "#E2CD9C" }} />
                          <span
                            className="text-[10px] font-black uppercase tracking-[0.15em]"
                            style={{ color: "#8A6516" }}
                          >
                            New since your last visit
                          </span>
                          <div className="h-px flex-1" style={{ backgroundColor: "#E2CD9C" }} />
                        </div>
                      )}

                      {/* A rule between groups, none after the last, and
                          no gap above the first — written out rather than
                          with first:/last:, which would key off position
                          inside this wrapper where every child is both. */}
                      <div
                        className="py-7"
                        style={{
                          paddingTop: i === 0 ? 0 : undefined,
                          borderBottom:
                            i < posts.length - 1 ? `1px solid ${LINE}` : undefined,
                        }}
                      >
                        <Comment post={post} ctx={ctx} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {problem && (
              <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3">
                <p className="text-xs font-bold text-rose-700">{problem}</p>
              </div>
            )}

            {/* ── Add a comment ───────────────────────────────────── */}
            {thread.is_locked ? (
              <div
                className="mt-8 flex items-center gap-3 rounded-lg border bg-gray-50 px-5 py-4"
                style={{ borderColor: LINE }}
              >
                <Lock className="h-4 w-4 text-gray-400" />
                <p className="text-[14px] font-bold text-gray-500">
                  This discussion is closed. No further comments can be added.
                </p>
              </div>
            ) : (
              <div
                className="mt-8 rounded-lg border bg-white px-6 py-6 md:px-8"
                style={{ borderColor: LINE }}
              >
                <h3 className="text-[17px] font-black" style={{ color: NAVY }}>
                  Join the discussion
                </h3>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  disabled={replying}
                  rows={5}
                  placeholder="Add your comment…"
                  className="mt-3 w-full rounded-lg border border-gray-200 px-4 py-3 text-[15px] leading-relaxed focus:border-gray-400 focus:outline-none"
                />
                <div className="mt-3">
                  <AttachmentPicker files={files} onChange={setFiles} disabled={replying} />
                </div>
                <div className="mt-4 flex justify-end">
                  <PrimaryButton onClick={postComment} disabled={replying}>
                    {replying ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Post comment
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>

          {/* ══ Aside ══════════════════════════════════════════════ */}
          <aside className="w-full shrink-0 lg:w-[320px]">
            <div className="border-t-[3px] bg-[#F2F3F6] px-6 py-6" style={{ borderTopColor: GOLD }}>
              <h2
                className="text-[13px] font-black uppercase tracking-[0.14em]"
                style={{ color: NAVY }}
              >
                Posted in
              </h2>
              <Link
                to={`/forum?category=${thread.category}`}
                className="mt-4 block text-[19px] font-black leading-tight hover:underline"
                style={{ color: NAVY }}
              >
                {thread.category_label || "General"}
              </Link>
              {category?.blurb && (
                <p className="mt-1 text-[13px] leading-snug text-gray-500">{category.blurb}</p>
              )}

              <h2
                className="mt-7 text-[13px] font-black uppercase tracking-[0.14em]"
                style={{ color: NAVY }}
              >
                Participants
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {people.map((p) => (
                  <Avatar key={p.id} person={p} size={44} />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
